import { ReloadOutlined } from '@ant-design/icons';
import { history } from '@umijs/max';
import { useSession } from '@zhiyue/core';
import { Spin } from 'antd';
import React from 'react';
import BootstrapStateView from '@/components/BootstrapStateView';
import { isPublicPortalPath, PARK_BOOTSTRAP_PATHS } from '@/constants/routes';
import {
  getParkBootstrapRoute,
  ParkBootstrapStatus,
  useParkBootstrap,
} from '@/services/parkBootstrap';

const bootstrapPaths = new Set<string>(PARK_BOOTSTRAP_PATHS);

const GateLoading: React.FC = () => (
  <div style={{ minHeight: 360, display: 'grid', placeItems: 'center' }}>
    <Spin size="large" />
  </div>
);

const ParkBootstrapGate: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { currentUser, loading: sessionLoading } = useSession();
  const { status, refresh } = useParkBootstrap();
  const pathname = history.location.pathname;
  const isBootstrapRoute = bootstrapPaths.has(pathname);
  const isPortalRoute = isPublicPortalPath(pathname);

  React.useEffect(() => {
    if (isPortalRoute) return;
    if (
      sessionLoading ||
      !currentUser ||
      status === ParkBootstrapStatus.INITIALIZING
    ) {
      return;
    }

    const target = getParkBootstrapRoute(status);

    if (isBootstrapRoute) {
      if (pathname !== target) {
        history.replace(target);
      }
      return;
    }

    if (status !== ParkBootstrapStatus.READY && pathname !== target) {
      history.replace(target);
    }
  }, [
    currentUser,
    isBootstrapRoute,
    isPortalRoute,
    pathname,
    refresh,
    sessionLoading,
    status,
  ]);

  if (isPortalRoute) return <>{children}</>;
  if (sessionLoading || !currentUser) return <>{children}</>;
  if (status === ParkBootstrapStatus.INITIALIZING) return <GateLoading />;
  if (isBootstrapRoute) return <>{children}</>;
  if (status === ParkBootstrapStatus.LOAD_ERROR) {
    return (
      <BootstrapStateView
        variant="error"
        icon={<ReloadOutlined />}
        title="园区列表加载失败"
        description="请重新加载园区信息后继续"
        action={{
          label: '重新加载',
          onClick: () => void refresh(),
        }}
      />
    );
  }
  if (status === ParkBootstrapStatus.READY) return <>{children}</>;
  return <GateLoading />;
};

export default ParkBootstrapGate;
